import { useAuth } from "@clerk/clerk-react";
import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation } from "convex/react";
import { formatDistanceToNow } from "date-fns";
import { MapPin, PackageCheck, Star } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { api } from "#/../convex/_generated/api";
import type { Id } from "#/../convex/_generated/dataModel";
import { ListingCard } from "#/components/listing-card";
import { Card, CardContent } from "#/components/ui/card";
import { useSignInModal } from "#/hooks/useSignInModal";
import { cn } from "#/lib/utils";

export const Route = createFileRoute("/sellers/$username")({
	head: ({ params }) => ({
		meta: [
			{ title: `@${params.username}'s Disc Golf Listings | Re:Disc` },
			{
				name: "description",
				content: `Browse disc golf discs listed by @${params.username} on Re:Disc. Contact the seller directly — no platform fees.`,
			},
			{ property: "og:title", content: `@${params.username} on Re:Disc` },
			{
				property: "og:description",
				content: `Browse disc golf discs listed by @${params.username} on Re:Disc.`,
			},
		],
	}),
	component: SellerProfilePage,
});

function SellerProfilePage() {
	const { t } = useTranslation("sellerProfile");
	const { username } = Route.useParams();
	const { isSignedIn } = useAuth();
	const { open: openSignIn } = useSignInModal();

	const { data: profile, isPending } = useQuery(
		convexQuery(api.users.getSellerProfile, { username }),
	);

	const { data: watchlistItems = [] } = useQuery(
		convexQuery(api.watchlist.getWatchlist, {}),
	);

	const savedIds = useMemo(
		() => new Set(watchlistItems.map((item) => item._id as string)),
		[watchlistItems],
	);

	const saveToWatchlist = useMutation(api.watchlist.saveToWatchlist);
	const removeFromWatchlist = useMutation(api.watchlist.removeFromWatchlist);

	function handleSaveToggle(listingId: Id<"listings">) {
		if (!isSignedIn) {
			openSignIn();
			return;
		}
		if (savedIds.has(listingId)) {
			removeFromWatchlist({ listingId });
		} else {
			saveToWatchlist({ listingId });
		}
	}

	if (isPending) {
		return (
			<div className="mx-auto max-w-5xl px-6 py-16">
				<div className="flex flex-col gap-4">
					<div className="h-4 w-24 animate-pulse rounded-full bg-muted" />
					<div className="h-14 w-56 animate-pulse rounded-xl bg-muted" />
					<div className="h-4 w-36 animate-pulse rounded-full bg-muted" />
				</div>
			</div>
		);
	}

	if (!profile) {
		return (
			<div className="mx-auto max-w-2xl px-6 py-24 text-center">
				<div
					className="pointer-events-none mb-8 flex justify-center opacity-10"
					aria-hidden
				>
					<DiscSilhouette />
				</div>
				<p className="mb-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">
					{t("notFound.code")}
				</p>
				<h1 className="display-title mb-3 text-[48px] leading-none text-foreground">
					{t("notFound.title")}
				</h1>
				<p className="text-muted-foreground">
					{t("notFound.description")}{" "}
					<span className="font-semibold text-foreground">@{username}</span>{" "}
					{t("notFound.exists")}
				</p>
			</div>
		);
	}

	const { seller, listings, soldCount, rating, reviews } = profile;
	const location = [seller.city, seller.country].filter(Boolean).join(", ");

	return (
		<div className="min-h-screen">
			{/* Profile header */}
			<div className="border-b border-border bg-background">
				<div className="mx-auto max-w-5xl px-6 py-14">
					<div className="flex flex-col gap-5">
						<p className="sec-tag rise-in" style={{ animationDelay: "0ms" }}>
							{t("header.tag")}
						</p>

						<h1
							className="rise-in display-title text-[clamp(48px,7vw,80px)] leading-none text-foreground"
							style={{ animationDelay: "60ms" }}
						>
							@{seller.username}
						</h1>

						<div
							className="rise-in flex flex-wrap items-center gap-4"
							style={{ animationDelay: "120ms" }}
						>
							{location && (
								<div className="flex items-center gap-1.5 text-sm text-muted-foreground">
									<MapPin className="h-3.5 w-3.5 shrink-0" />
									<span>{location}</span>
								</div>
							)}
							<div className="flex items-center gap-1.5 text-sm text-muted-foreground">
								<PackageCheck className="h-3.5 w-3.5 shrink-0" />
								<span>
									{soldCount === 0
										? t("soldCount.none")
										: soldCount === 1
											? t("soldCount.one")
											: t("soldCount.other", { count: soldCount })}
								</span>
							</div>
							{rating.count > 0 && rating.averageRating !== null && (
								<div className="flex items-center gap-1 text-sm font-medium text-foreground">
									<Star className="h-3.5 w-3.5 fill-primary text-primary shrink-0" />
									<span>{rating.averageRating.toFixed(1)}</span>
									<span className="font-normal text-muted-foreground">
										· {t("reviewCount", { count: rating.count })}
									</span>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>

			{/* Listings grid */}
			<div className="mx-auto max-w-5xl px-6 py-12">
				{listings.length === 0 ? (
					<div className="rounded-[var(--radius)] border border-dashed border-border py-20 text-center text-muted-foreground">
						{t("listings.empty")}
					</div>
				) : (
					<>
						<p className="mb-6 text-xs font-bold uppercase tracking-widest text-muted-foreground">
							{listings.length === 1
								? t("listings.count_one")
								: t("listings.count_other", { count: listings.length })}
						</p>
						<div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
							{listings.map((listing) => (
								<ListingCard
									key={listing._id}
									listing={listing}
									isSaved={savedIds.has(listing._id)}
									onSaveToggle={() => handleSaveToggle(listing._id)}
								/>
							))}
						</div>
					</>
				)}
			</div>

			{/* Reviews section */}
			<div className="mx-auto max-w-5xl px-6 pb-16">
				<div className="border-t border-border pt-12">
					<h2 className="display-title mb-6 text-[28px] leading-none text-foreground">
						{t("reviews.title")} ({rating.count})
					</h2>

					{reviews.length === 0 ? (
						<p className="text-sm text-muted-foreground">
							{t("reviews.empty")}
						</p>
					) : (
						<div className="grid gap-4 sm:grid-cols-2">
							{reviews.slice(0, 10).map((review) => (
								<ReviewCard key={review._id} review={review} />
							))}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

type ReviewItem = {
	_id: string;
	reviewerUsername: string | null;
	rating: number;
	comment: string | undefined;
	_creationTime: number;
};

function ReviewCard({ review }: { review: ReviewItem }) {
	const relativeTime = formatDistanceToNow(new Date(review._creationTime), {
		addSuffix: true,
	});

	return (
		<Card className="gap-3 py-4">
			<CardContent className="flex flex-col gap-2 px-4">
				{/* Header: username + date */}
				<div className="flex items-center justify-between gap-2">
					<span className="text-sm font-semibold text-foreground">
						{review.reviewerUsername
							? `@${review.reviewerUsername}`
							: "Anonymous"}
					</span>
					<span className="text-xs text-muted-foreground">{relativeTime}</span>
				</div>

				{/* Stars */}
				<div
					className="flex gap-0.5"
					role="img"
					aria-label={`${review.rating} out of 5 stars`}
				>
					{Array.from({ length: 5 }).map((_, i) => (
						<Star
							// biome-ignore lint/suspicious/noArrayIndexKey: static star icons
							key={i}
							className={cn(
								"size-3.5",
								i < review.rating
									? "fill-primary text-primary"
									: "fill-transparent text-muted-foreground/30",
							)}
						/>
					))}
				</div>

				{/* Comment */}
				{review.comment && (
					<p className="text-sm leading-relaxed text-foreground/80">
						{review.comment}
					</p>
				)}
			</CardContent>
		</Card>
	);
}

function DiscSilhouette() {
	return (
		<div className="relative h-32 w-32">
			<div className="h-full w-full rounded-full border-[12px] border-foreground" />
			<div className="absolute inset-[18px] rounded-full border-[4px] border-foreground" />
			<div className="absolute left-1/2 top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-foreground" />
		</div>
	);
}
